import dayjs from 'dayjs';

import {
  ProviderAuditFlow,
  ProviderAuditLockFilters,
  ProviderAuditLockState,
  ProviderAuditMismatchFilters,
  ProviderAuditMismatchItem,
} from 'src/pages/admin/services/xbitRelayProviderAuditService';

export type MismatchFilterForm = {
  userId?: string;
  provider?: string;
  timeFrom?: number | string;
  timeTo?: number | string;
};

/** `0` is a valid unix-ms boundary, so only undefined/null/'' are dropped. */
const toUnixMs = (value?: number | string | null): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const pickMismatchFilters = (
  filter?: MismatchFilterForm
): ProviderAuditMismatchFilters => {
  if (!filter) return {};
  const out: ProviderAuditMismatchFilters = {};
  if (filter.userId) out.userId = filter.userId;
  if (filter.provider) out.provider = filter.provider;

  const timeFrom = toUnixMs(filter.timeFrom);
  if (timeFrom !== undefined) out.timeFrom = timeFrom;
  const timeTo = toUnixMs(filter.timeTo);
  if (timeTo !== undefined) out.timeTo = timeTo;

  return out;
};

export type LockFilterForm = {
  userId?: string;
  provider?: string;
};

export const pickLockFilters = (filter?: LockFilterForm): ProviderAuditLockFilters => {
  if (!filter) return {};
  const out: ProviderAuditLockFilters = {};
  if (filter.userId) out.userId = filter.userId;
  if (filter.provider) out.provider = filter.provider;
  return out;
};

/** `/locks` has no id of its own — (user, provider) is the natural key for a row. */
export const lockRowId = (
  state: Pick<ProviderAuditLockState, 'userId' | 'provider'>
): string => `${state.userId}:${state.provider}`;

/** `/locks` is not paginated, so the page slice is taken client-side. */
export const paginateLocks = <T,>(rows: T[], page: number, perPage: number): T[] => {
  if (perPage <= 0) return rows;
  const safePage = page > 0 ? page : 1;
  const start = (safePage - 1) * perPage;
  return rows.slice(start, start + perPage);
};

/** int64-as-string timestamps: `Number(...)` before dayjs, `-` when absent. */
export const formatUnixMs = (
  value?: string | number | null,
  pattern = 'YYYY-MM-DD HH:mm:ss'
): string => {
  const ms = toUnixMs(value);
  if (ms === undefined) return '-';
  return dayjs(ms).format(pattern);
};

/**
 * Amounts are decimal strings and there is no decimal lib in this repo — inspect the digits
 * instead of going through `parseFloat`, which would lose precision on large int64 values.
 * Anything that is not a plain decimal is reported as non-zero so it stays visible.
 */
export const isZeroAmount = (value?: string | null): boolean => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return true;
  const digits = trimmed.replace(/^[+-]/, '').replace('.', '');
  if (!/^\d+$/.test(digits)) return false;
  return !/[1-9]/.test(digits);
};

export const isNegativeAmount = (value?: string | null): boolean =>
  (value ?? '').trim().startsWith('-') && !isZeroAmount(value);

export const formatAmount = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  return trimmed || '0';
};

export const normalizeFlow = (value?: string | null): ProviderAuditFlow =>
  value === 'withdraw' ? 'withdraw' : 'deposit';

export const residualForFlow = (
  record: Pick<ProviderAuditMismatchItem, 'residualDeposit' | 'residualWithdraw'>,
  flow: ProviderAuditFlow
): string =>
  formatAmount(flow === 'withdraw' ? record.residualWithdraw : record.residualDeposit);

export const hasResidual = (
  record: Pick<ProviderAuditMismatchItem, 'residualDeposit' | 'residualWithdraw'>
): boolean =>
  !isZeroAmount(record.residualDeposit) || !isZeroAmount(record.residualWithdraw);

export const truncateMiddle = (value?: string | null, head = 8, tail = 6): string =>
  value && value.length > head + tail + 3
    ? `${value.slice(0, head)}...${value.slice(-tail)}`
    : value || '-';

/** These services use the bare axios instance, so failures carry `response.data`. */
type AxiosLikeError = { response?: { data?: { message?: string } }; message?: string };

export const errorMessage = (error: AxiosLikeError | undefined, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;

/** `<input type="datetime-local">` emits local wall-clock time; send it out as unix ms. */
export const datetimeLocalToUnixMs = (value?: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : undefined;
};
